// forwardsTableLogic.js
import { addDays, daysBetweenDates, getToday, parseDate } from '../../utils/dateFunctions';
import {
  computeForwardValueFromDevaluationAndSpot,
  getGeneratorDevaluationFunctionByDay
} from '../../utils/forwardFunctions';
import * as con from '../../GlobalConstants';


export const createColumn = (refSpot, forwardCurve) => {
  const today = getToday().toISODate()
  const expiryDays = 90
  const expirationDate = addDays(parseDate(today), expiryDays).toISODate()

  const devalFn = getGeneratorDevaluationFunctionByDay(
    forwardCurve[con.DAYS],
    forwardCurve[con.BUY]
  )
  const bankDevaluation = devalFn(expiryDays)
  const bankRate = computeForwardValueFromDevaluationAndSpot(bankDevaluation, refSpot, expiryDays)

  return {
    counterparty: con.BLANK,
    type: con.BUY,
    executionDate: today,
    spot: refSpot,
    spotManual: false,
    nominal: 250000,
    expiryDays,
    expirationDate,
    maturityInput: con.DAYS,   // "days" | "date"
    bankSpot: refSpot,
    bankDevaluation,
    bankRate,
    bankInput: con.RATE,       // "rate" | "devaluation"
  }
}


export const deriveColumn = (col, forwardCurve) => {
  const curveValues = forwardCurve[col.type === con.BUY ? con.BUY : con.SELL]
  const devalFn = getGeneratorDevaluationFunctionByDay(forwardCurve[con.DAYS], curveValues)

  const refDevaluation = devalFn(col.expiryDays)
  const refRate = computeForwardValueFromDevaluationAndSpot(refDevaluation, col.spot, col.expiryDays)

  const sign = col.type === con.BUY ? 1 : -1

  const fwdPoints        = sign * (col.bankRate - col.spot) * 10000
  const diffFwdPoints    = sign * (refDevaluation - col.bankDevaluation)
  const diffCurvePxD     = computeForwardValueFromDevaluationAndSpot(col.bankDevaluation, col.bankSpot, col.expiryDays) - refRate
  const diffSpotPxD      = sign * (col.spot - col.bankSpot)
  const diffTotalPxD     = diffSpotPxD + diffCurvePxD
  const diffTotal        = diffTotalPxD * col.nominal

  return {
    refDevaluation,
    refRate,
    fwdPoints,
    diffFwdPoints,
    diffCurvePxD,
    diffSpotPxD,
    diffTotalPxD,
    diffTotal,
  }
}

// ─── Updaters — cada uno recibe la columna y retorna la columna actualizada ──

export const updateMaturity = (col, field, value) => {
  if (field === con.DAYS) {
    const expiryDays = parseInt(value) || 0
    return { 
      ...col, 
      expiryDays, 
      expirationDate: addDays(parseDate(col.executionDate), expiryDays).toISODate(),
      maturityInput: con.DAYS 
    }
  } else {
    const expiryDays = daysBetweenDates(col.executionDate, value, false)
    return { ...col, expirationDate: value, expiryDays, maturityInput: con.DATE }
  }
}

export const updateExecutionDate = (col, value) => {
  if (col.maturityInput === con.DAYS) {
    return { 
      ...col, 
      executionDate: value, 
      expirationDate: addDays(parseDate(value), col.expiryDays).toISODate() 
    }
  } else {
    const expiryDays = daysBetweenDates(value, col.expirationDate, false)
    return { ...col, executionDate: value, expiryDays }
  }
}

export const updateBankQuote = (col, field, value) => {
  if (field === con.RATE) {
    // Recalcula devaluation desde rate
    // bankDevaluation se despeja: rate = bankSpot * (1 + dev)^(days/365)
    const days = col.expiryDays
    const dev = Math.pow(value / col.bankSpot, con.DAYS_IN_YEAR_FORWARDS / days) - 1
    return { ...col, bankRate: value, bankDevaluation: isNaN(dev) || dev < 0 ? 0 : dev, bankInput: con.RATE }
  } else {
    const rate = computeForwardValueFromDevaluationAndSpot(value, col.bankSpot, col.expiryDays)
    return { ...col, bankDevaluation: value, bankRate: rate, bankInput: "devaluation" }
  }
}

export const updateBankSpot = (col, value) => {
  // Mantiene el input activo (rate o devaluation) y recalcula el otro
  if (col.bankInput === con.RATE) {
    const dev = Math.pow(col.bankRate / value, con.DAYS_IN_YEAR_FORWARDS / col.expiryDays) - 1
    return { ...col, bankSpot: value, bankDevaluation: isNaN(dev) || dev < 0 ? 0 : dev }
  } else {
    const rate = computeForwardValueFromDevaluationAndSpot(col.bankDevaluation, value, col.expiryDays)
    return { ...col, bankSpot: value, bankRate: rate }
  }
}